import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Form } from 'react-bootstrap';

import type { IFormSelectGroupOption } from '@/components/common/form/types';

import { flex, media } from '@/styles/theme';
import { usePathParam } from '@/hooks';
import { PathParams } from '@/store/enums';

export interface IContractSelect {
  contractId: number;
  displayName: string;
  isCurrentContract: boolean;
  contractEndBlock?: number | null;
}

interface IProps {
  name: string;
  contractsList: IContractSelect[];
  externalHandling?: {
    setContractId: (contractId: number) => void;
    contractId: number | null;
  };
}

const ContractSelect = ({ name, contractsList, externalHandling }: IProps): React.ReactElement => {
  const parsedContracts = useMemo(
    () =>
      contractsList
        .filter(contract => !contract.contractEndBlock || contract.isCurrentContract)
        .map(item => ({
          value: item.contractId,
          label: item.displayName,
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1)),
    [contractsList],
  );

  const [contractId, setContractId] = usePathParam(PathParams.contractId);
  const [options, setOptions] = useState<IFormSelectGroupOption[]>([]);

  useEffect(() => {
    if (externalHandling) return;
    if (!contractId && parsedContracts?.[0]?.value) setContractId(parsedContracts?.[0]?.value);
  }, [contractId, externalHandling, parsedContracts, setContractId]);

  useEffect(() => {
    setOptions(parsedContracts);
  }, [parsedContracts]);

  const handleChange = (id: number) => {
    if (!externalHandling) return setContractId(id);

    externalHandling.setContractId(Number(id));
  };

  return (
    <StyledSelect
      as="select"
      id={name}
      name={name}
      options={options}
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handleChange(Number(event.target.value))}
      value={externalHandling?.contractId ?? contractId}
    >
      {options.map((option, index) => (
        <option key={index} value={option.value}>
          {option.label}
        </option>
      ))}
    </StyledSelect>
  );
};

const Container = styled.div.attrs({
  className: `${flex.columnCenter}`,
})`
  ${media.tablet} {
    flex-direction: row !important;
    align-items: center;
  }
`;

const Label = styled.div`
  margin-right: 32px;
  margin-bottom: 11px;
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: #4b5563;

  ${media.tablet} {
    flex-direction: row !important;
    align-items: center;
    margin-bottom: 0;
  }
`;

const StyledSelect = styled(Form.Control)`
  width: 100%;
  min-width: 200px;
  margin-top: 3px;
  padding-left: 10px;
  height: 46px;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  padding-right: 30px !important;
  border: 1px solid #ced4da;
  box-sizing: border-box;
  border-radius: 8px;

  background-color: #f9fafb;
  background-image: linear-gradient(45deg, transparent 50%, gray 50%),
    linear-gradient(135deg, gray 50%, transparent 50%), linear-gradient(to right, #ccc, #ccc);
  background-position: calc(100% - 20px) calc(1em + 4px), calc(100% - 15px) calc(1em + 4px), calc(100% - 2.5em) 0.5em;
  background-size: 5px 5px, 5px 5px, 0 1.5em;
  background-repeat: no-repeat;
`;

ContractSelect.Container = Container;
ContractSelect.Label = Label;

export { ContractSelect };
